/**
 * contract-view.js — hydrate the signed layaway agreement from its snapshot.
 *
 * Reads ?t=<token>, fetches the frozen document, and writes it into the markup.
 *
 * RULES THIS FILE FOLLOWS, because the whole point is that the page cannot lie:
 *   - It NEVER computes a payment, a total or a date. Every number rendered is a
 *     number the snapshot already contains. If a figure is missing it shows an
 *     em dash, not a guess.
 *   - Money comes from the snapshot in CENTS and is formatted with the currency
 *     the snapshot names, not the shop's current currency.
 *   - Terms are inserted as TEXT, never as HTML. They are a legal artefact from
 *     the database; treating them as markup would make a stored value executable.
 *   - No retry-on-404. A missing token is an answer, not a transient failure.
 *
 * Written as ES5-compatible script (no modules, no optional chaining) to match the
 * rest of this theme's assets, which are served unbundled.
 */
(function () {
  'use strict';

  var root = document.querySelector('.contract-view');
  if (!root) return;

  var endpoint = (root.getAttribute('data-endpoint') || '').replace(/\/$/, '');

  // Unlocks the print rules that strip the theme chrome down to the document.
  document.documentElement.classList.add('contract-view-active');

  function show(state) {
    var all = root.querySelectorAll('[data-state]');
    for (var i = 0; i < all.length; i++) {
      all[i].hidden = all[i].getAttribute('data-state') !== state;
    }
  }

  function set(field, value) {
    var nodes = root.querySelectorAll('[data-f="' + field + '"]');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = value === null || value === undefined || value === '' ? '—' : value;
    }
  }

  /**
   * Payment cadence in words, from the snapshot's intervalDays.
   * Returns null for a missing/odd interval so `set` renders an em dash rather
   * than asserting a cadence this agreement does not actually specify.
   */
  function cadenceLabel(intervalDays) {
    if (intervalDays === 7) return 'Every week';
    if (intervalDays === 14) return 'Every 2 weeks';
    if (typeof intervalDays === 'number' && intervalDays > 0) return 'Every ' + intervalDays + ' days';
    return null;
  }

  /** Cents -> localised currency. Falls back to a plain figure if Intl is unhappy. */
  function money(cents, currency) {
    if (typeof cents !== 'number' || isNaN(cents)) return null;
    var code = (currency || 'usd').toUpperCase();
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /** ISO date (YYYY-MM-DD) -> "Tue 12 Aug 2026", parsed as UTC so it cannot shift a day. */
  function dateLong(iso) {
    if (!iso) return null;
    var d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso + 'T00:00:00Z' : iso);
    if (isNaN(d.getTime())) return null;
    try {
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC'
      }).format(d);
    } catch (e) {
      return iso;
    }
  }

  function dateTimeLong(iso, tz) {
    if (!iso) return null;
    var d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    try {
      return new Intl.DateTimeFormat('en-US', {
        day: 'numeric', month: 'long', year: 'numeric',
        hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
        timeZone: tz || 'America/New_York'
      }).format(d);
    } catch (e) {
      return d.toISOString();
    }
  }

  function lines(el, arr) {
    if (!el) return;
    el.textContent = '';
    if (!arr || !arr.length) return;
    for (var i = 0; i < arr.length; i++) {
      if (i) el.appendChild(document.createElement('br'));
      el.appendChild(document.createTextNode(arr[i]));
    }
  }

  function renderEvidence(payload) {
    var ev = payload.evidence;
    if (!ev) return;
    var c = payload.contract || {};

    var authBlock = root.querySelector('[data-evidence-auth-block]');
    var dl = root.querySelector('[data-evidence-auth]');
    var inst = ev.instrument;
    var consent = (ev.consents && ev.consents.length) ? ev.consents[ev.consents.length - 1] : null;
    if (!authBlock || !dl || (!inst && !consent)) return;
    authBlock.hidden = false;
    dl.textContent = '';

    function row(label, value) {
      if (value === null || value === undefined || value === '') return;
      var div = document.createElement('div');
      var dt = document.createElement('dt');
      dt.textContent = label;
      var dd = document.createElement('dd');
      dd.textContent = value;
      div.appendChild(dt); div.appendChild(dd);
      dl.appendChild(div);
    }

    if (inst) {
      var instLabel = [inst.brand, inst.last4 ? '•••• ' + inst.last4 : null].filter(Boolean).join(' ');
      row('Payment instrument', instLabel || inst.type);
      row('Expires', inst.expMonth && inst.expYear ? inst.expMonth + '/' + inst.expYear : null);
      // Stripe's stable identifier for the physical instrument — the same card
      // always yields the same fingerprint. The figure a dispute response cites.
      row('Instrument fingerprint', inst.fingerprint);
      row('Processor', inst.psp);
      row('Processor customer', inst.pspCustomerId);
      row('Processor payment method', inst.pspPaymentMethod);
      row('Saved on', dateTimeLong(inst.vaultedAt, c.timezone));
    }
    if (consent) {
      row('Authorized on', dateTimeLong(consent.acceptedAt, c.timezone));
      row('Authorization IP', consent.ip);
      row('Device', consent.deviceId);
      row('CVC check', consent.cvcResult);
      row('ZIP check', consent.avsResult);
      row('Network transaction', consent.networkTxnId);
    }
  }

  function render(payload) {
    var c = payload.contract || {};
    var cur = c.currency;
    var plan = c.plan || {};
    var totals = c.totals || {};

    // ---- superseded banner. Must be obvious: a replaced agreement pretending to
    // be current is the one failure mode worse than not loading at all.
    var sup = root.querySelector('[data-superseded]');
    if (payload.superseded && sup) {
      sup.hidden = false;
      var detail = root.querySelector('[data-superseded-detail]');
      if (detail) {
        detail.textContent = ' Replaced on ' + (dateLong(payload.superseded.at) || '—')
          + (payload.superseded.reason ? ' (' + payload.superseded.reason + ').' : '.')
          + ' Ask us for the current version.';
      }
    }

    // ---- header
    set('orderReference', c.orderReference);
    set('shopifyOrderNumber', c.shopifyOrderNumber);
    set('signedAt', dateLong((payload.signature && payload.signature.signedAt) || c.placedAt));

    // ---- parties
    var m = c.merchant || {};
    set('merchantName', m.name);
    lines(root.querySelector('[data-f="merchantAddress"]'), m.address);
    set('merchantEmail', m.contactEmail);

    var cust = c.customer || {};
    set('customerName', cust.name);
    lines(root.querySelector('[data-f="customerAddress"]'), cust.address);
    set('customerEmail', cust.email);
    set('customerPhone', cust.phone);

    // Where it ships, when that is not the signer (schemaVersion 2). Older
    // snapshots carry no delivery block and the section stays hidden.
    var del = c.delivery || null;
    var giftBlock = root.querySelector('[data-f="shipsToBlock"]');
    if (giftBlock) {
      if (del && del.name) {
        giftBlock.hidden = false;
        set('deliveryName', del.name);
        lines(root.querySelector('[data-f="deliveryAddress"]'), del.address);
        set('deliveryPhone', del.phone);
        set('giftNote', (c.gift && c.gift.isGift) ? 'A gift. The plan and its payments stay with the customer above.' : '');
      } else {
        giftBlock.hidden = true;
      }
    }

    // ---- summary. Every value straight from the snapshot's own plan block.
    // The cadence label is derived, not written into the markup. It was hardcoded
    // "Every 2 weeks" and survived the move to $15/week, so a signed agreement
    // showed a fortnightly cadence next to a 7-day schedule. Reading intervalDays
    // off the snapshot means the label cannot drift from the plan again.
    set('cadence', cadenceLabel(plan.intervalDays));
    set('installment', money(plan.installmentCents, cur));
    set('weeks', typeof plan.weeks === 'number'
      ? plan.weeks + (plan.monthsApprox ? ' · about ' + plan.monthsApprox + ' months' : '')
      : null);
    // "Paid today": what the signed agreement says was charged when the order was placed —
    // the first payment since pay-at-checkout (2026-10-05, plan.paidAtCheckoutCents), and the
    // deposit (always $0) on every agreement signed before.
    set('deposit', money(plan.paidAtCheckoutCents || plan.depositCents, cur));
    set('total', money(plan.totalCents, cur));

    // ---- items
    var list = root.querySelector('[data-items]');
    if (list) {
      list.textContent = '';
      var items = c.items || [];
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        var li = document.createElement('li');
        li.className = 'contract-view__item';

        if (it.imageUrl) {
          var fig = document.createElement('div');
          fig.className = 'contract-view__item-media';
          var img = document.createElement('img');
          img.src = it.imageUrl;
          img.alt = '';
          img.loading = 'lazy';
          fig.appendChild(img);
          li.appendChild(fig);
        }

        var body = document.createElement('div');
        body.className = 'contract-view__item-body';

        var t = document.createElement('p');
        t.className = 'contract-view__item-title';
        // Frozen title — deliberately NOT a link to the live product, whose name
        // and price may since have changed. The contract records what was sold.
        t.textContent = it.title + (it.variantTitle ? ' — ' + it.variantTitle : '');
        body.appendChild(t);

        var meta = document.createElement('p');
        meta.className = 'contract-view__item-meta';
        var bits = [];
        if (it.sku) bits.push('SKU ' + it.sku);
        if (it.quantity) bits.push('Qty ' + it.quantity);
        if (typeof it.unitPriceCents === 'number') bits.push(money(it.unitPriceCents, cur) + ' each');
        meta.textContent = bits.join(' · ');
        body.appendChild(meta);

        li.appendChild(body);

        var amt = document.createElement('p');
        amt.className = 'contract-view__item-amount';
        amt.textContent = money(it.lineTotalCents, cur) || '—';
        li.appendChild(amt);

        list.appendChild(li);
      }
    }

    set('subtotal', money(totals.subtotalCents, cur));
    set('totalAgain', money(totals.totalCents, cur));

    var shipRow = root.querySelector('[data-row="shipping"]');
    if (shipRow && typeof totals.shippingCents === 'number' && totals.shippingCents > 0) {
      shipRow.hidden = false;
      set('shipping', money(totals.shippingCents, cur));
    }
    var taxRow = root.querySelector('[data-row="tax"]');
    // null tax means none was charged, which is a fact worth not implying otherwise.
    if (taxRow && typeof totals.taxCents === 'number') {
      taxRow.hidden = false;
      set('tax', money(totals.taxCents, cur));
    }

    // ---- schedule
    var tbody = root.querySelector('[data-schedule]');
    var schedule = c.schedule || [];
    if (tbody) {
      tbody.textContent = '';
      var sum = 0;
      for (var s = 0; s < schedule.length; s++) {
        var p = schedule[s];
        sum += (typeof p.amountCents === 'number' ? p.amountCents : 0);
        var tr = document.createElement('tr');
        var th = document.createElement('th');
        th.setAttribute('scope', 'row');
        th.textContent = p.n;
        var td1 = document.createElement('td');
        td1.textContent = dateLong(p.dueDate) || p.dueDate || '—';
        var td2 = document.createElement('td');
        td2.className = 'ta-right';
        td2.textContent = money(p.amountCents, cur) || '—';
        tr.appendChild(th); tr.appendChild(td1); tr.appendChild(td2);
        tbody.appendChild(tr);
      }
      // Summed from the frozen rows, not recomputed from the plan — if the two
      // ever disagreed, showing the sum of what is listed is the honest number.
      set('scheduleTotal', money(sum, cur));
    }

    // ---- prose blocks. textContent, not innerHTML: these are stored values.
    var fee = root.querySelector('[data-f="feePolicy"]');
    if (fee) fee.textContent = c.feePolicy || '';

    var terms = c.terms || payload.terms || {};
    set('termsVersion', terms.version);
    set('termsHash', terms.sha256 ? terms.sha256.slice(0, 16) : null);
    var tt = root.querySelector('[data-f="termsText"]');
    if (tt) {
      tt.textContent = '';
      var paras = String(terms.text || '').split(/\n{2,}/);
      for (var q = 0; q < paras.length; q++) {
        if (!paras[q].trim()) continue;
        var pEl = document.createElement('p');
        pEl.textContent = paras[q].trim();
        tt.appendChild(pEl);
      }
    }

    // ---- what the signature covered: the consents the snapshot records
    var consents = c.consents || null;
    var consentsBlock = root.querySelector('[data-consents-block]');
    var consentsList = root.querySelector('[data-consents]');
    if (consents && consentsBlock && consentsList) {
      var agreed = [];
      if (consents.terms) agreed.push('The Layaway Agreement' + (terms.version ? ', version ' + terms.version : ''));
      if (consents.cancellationPolicy) agreed.push('The cancellation policy');
      if (consents.electronicRecords) agreed.push('Receiving this agreement, the payment authorization, the schedule, notices and receipts electronically');
      if (consents.paymentAuthorization) agreed.push('Authorization to charge the saved card for the payments in the schedule');
      consentsList.innerHTML = '';
      agreed.forEach(function (text) { var li = document.createElement('li'); li.textContent = text; consentsList.appendChild(li); });
      consentsBlock.hidden = agreed.length === 0;
    }

    // ---- signature
    var sig = payload.signature || (c.signature || {});
    set('signatureName', sig.name);
    set('signedAtFull', dateTimeLong(sig.signedAt, c.timezone));
    var sigImg = root.querySelector('[data-sig-image]');
    var sigMissing = root.querySelector('[data-sig-missing]');
    if (sig.imageUrl && sigImg) {
      sigImg.src = sig.imageUrl;
      sigImg.hidden = false;
    } else if (sigMissing) {
      sigMissing.hidden = false;
    }

    renderEvidence(payload);

    set('issuedAt', dateLong(payload.issuedAt));

    show('doc');
  }

  function load() {
    var token = new URLSearchParams(window.location.search).get('t');
    if (!token) { show('missing'); return; }

    show('loading');
    fetch(endpoint + '/api/public/contract/' + encodeURIComponent(token), {
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        if (res.status === 404) { show('missing'); return null; }
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (payload) {
        if (!payload) return;
        if (!payload.contract) { show('missing'); return; }
        render(payload);
      })
      .catch(function (err) {
        // Never fall back to live data here. A contract that cannot be loaded is
        // an error; a contract assembled from today's order would be a forgery.
        if (window.console) console.error('[contract-view]', err);
        show('error');
      });
  }

  var retry = root.querySelector('[data-retry]');
  if (retry) retry.addEventListener('click', load);

  var printBtn = root.querySelector('[data-print]');
  if (printBtn) printBtn.addEventListener('click', function () { window.print(); });

  // The schedule ships collapsed on screen, but the printed/PDF copy must be
  // the complete document — expand every <details> for print and restore the
  // reader's state afterwards. beforeprint also covers Cmd/Ctrl+P.
  var openedForPrint = [];
  window.addEventListener('beforeprint', function () {
    openedForPrint = [];
    var all = root.querySelectorAll('details[data-collapse]');
    for (var i = 0; i < all.length; i++) {
      if (!all[i].open) { all[i].open = true; openedForPrint.push(all[i]); }
    }
  });
  window.addEventListener('afterprint', function () {
    for (var i = 0; i < openedForPrint.length; i++) openedForPrint[i].open = false;
    openedForPrint = [];
  });

  load();
})();
